import React, { useState, useEffect } from 'react';
import { 
  Layout, Typography, Card, Button, message, 
  Spin, Tabs, Divider, Space, Result 
} from 'antd';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeftOutlined, SaveOutlined, 
  FileImageOutlined, MessageOutlined, ExperimentOutlined 
} from '@ant-design/icons';
import { workflowAPI } from '../services/api';
import ImageUploadNode from '../components/ImageUploadNode';
import SamplerNode from '../components/SamplerNode';
import StringVariableNode from '../components/StringVariableNode';
import AppHeader from '../components/AppHeader';

const { Content } = Layout;
const { Title, Text } = Typography;

const WorkflowDetailPage = () => {
  const { workflowNum } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [workflow, setWorkflow] = useState(null);
  const [editableNodes, setEditableNodes] = useState({
    imageNodes: [],
    stringNodes: [],
    promptNodes: {
      fixed: [],
      variable: [],
      background: [],
      negative: [],
      general: []
    },
    samplerNodes: []
  });
  const [modifications, setModifications] = useState({});
  
  // 获取工作流详情
  const fetchWorkflowDetail = async () => {
    try {
      setLoading(true);
      
      const response = await workflowAPI.getWorkflowDetail(workflowNum);
      
      if (response.success) {
        setWorkflow(response.data.workflow_info);
        setEditableNodes(response.data.editable_nodes);
        
        // 如果已有修改，从详情中加载现有修改
        if (response.data.has_modifications && response.data.existing_modifications) {
          // 加载现有修改，但不覆盖用户当前正在进行的修改
          setModifications(prevMods => {
            // 合并已有修改和用户当前的修改，优先保留用户当前修改
            return {
              ...response.data.existing_modifications,
              ...prevMods
            };
          });
          console.log('已加载现有工作流修改记录');
        }
      } else {
        message.error('获取工作流详情失败');
      }
    } catch (error) {
      console.error('获取工作流详情失败:', error);
      message.error('获取工作流详情失败: ' + (error.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  };
  
  // 组件加载时获取工作流详情
  useEffect(() => {
    if (workflowNum) {
      fetchWorkflowDetail();
    }
  }, [workflowNum]);
  
  // 处理节点变更
  const handleNodeChange = (nodeId, changes) => {
    setModifications(prev => {
      const updatedModifications = { ...prev };
      
      // 如果节点已存在，更新其变更；否则创建新节点
      if (updatedModifications[nodeId]) {
        updatedModifications[nodeId] = {
          ...updatedModifications[nodeId],
          ...changes
        };
      } else {
        updatedModifications[nodeId] = changes;
      }
      
      return updatedModifications;
    });
  };
  
  // 保存修改
  const handleSaveModifications = async () => {
    try {
      setSaving(true);
      
      if (Object.keys(modifications).length === 0) {
        message.info('没有修改需要保存');
        return;
      }
      
      const response = await workflowAPI.saveWorkflowModifications(
        workflowNum,
        modifications
      );
      
      if (response.success) {
        message.success('工作流修改已保存');
        
        // 重新获取最新数据
        await fetchWorkflowDetail();
        
        // 不清空修改记录，保持用户界面的一致性
        // setModifications({});
      } else {
        message.error('保存工作流修改失败');
      }
    } catch (error) {
      console.error('保存工作流修改失败:', error);
      message.error('保存工作流修改失败: ' + (error.message || '未知错误'));
    } finally {
      setSaving(false);
    }
  };
  
  // 返回首页
  const handleGoBack = () => {
    navigate('/');
  };
  
  // 渲染加载中状态
  if (loading) {
    return (
      <Layout style={{ minHeight: '100vh' }}>
        <AppHeader />
        <Content style={{ padding: '24px 50px' }}>
          <div style={{ textAlign: 'center', marginTop: 100 }}>
            <Spin size="large" />
            <p style={{ marginTop: 20 }}>正在加载工作流详情...</p>
          </div>
        </Content>
      </Layout>
    );
  }
  
  // 渲染错误状态
  if (!workflow) {
    return (
      <Layout style={{ minHeight: '100vh' }}>
        <AppHeader />
        <Content style={{ padding: '24px 50px' }}>
          <Result
            status="error"
            title="工作流不存在"
            subTitle="无法加载指定的工作流，它可能不存在或已被删除。"
            extra={
              <Button type="primary" onClick={handleGoBack}>
                返回首页
              </Button>
            }
          />
        </Content>
      </Layout>
    );
  }
  
  return (
    <Layout style={{ minHeight: '100vh' }}>
      <AppHeader />
      <Content style={{ padding: '24px 50px' }}>
        <Space style={{ marginBottom: 16 }}>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={handleGoBack}
          >
            返回列表
          </Button>
          
          <Button 
            type="primary" 
            icon={<SaveOutlined />} 
            onClick={handleSaveModifications}
            loading={saving}
            disabled={Object.keys(modifications).length === 0}
          >
            保存修改
          </Button>
        </Space>
        
        <Card>
          <Title level={3}>{workflow.workflow_name}</Title>
          <Text>工作流编号: {workflow.workflow_num}</Text>
          <Divider />
          
          <Tabs defaultActiveKey="1">
            <Tabs.TabPane 
              tab={
                <span>
                  <FileImageOutlined />
                  图像节点 ({editableNodes.imageNodes.length})
                </span>
              } 
              key="1"
            >
              {editableNodes.imageNodes.length > 0 ? (
                editableNodes.imageNodes.map(node => (
                  <ImageUploadNode 
                    key={node.nodeId} 
                    node={node} 
                    onNodeChange={handleNodeChange} 
                  />
                ))
              ) : (
                <Text type="secondary">没有找到图像节点</Text>
              )}
            </Tabs.TabPane>

            <Tabs.TabPane
              tab={
                <span>
                  <MessageOutlined />
                  文字变量节点 ({editableNodes.stringNodes.length})
                </span>
              }
              key="string"
            >
              {editableNodes.stringNodes.length > 0 ? (
                editableNodes.stringNodes.map(node => (
                  <StringVariableNode
                    key={node.nodeId}
                    node={node}
                    onNodeChange={handleNodeChange}
                  />
                ))
              ) : (
                <Text type="secondary">没有找到可配置的文字变量或提示词节点</Text>
              )}
            </Tabs.TabPane>
            
            <Tabs.TabPane 
              tab={
                <span>
                  <ExperimentOutlined />
                  采样器节点 ({editableNodes.samplerNodes.length})
                </span>
              } 
              key="3"
            >
              {editableNodes.samplerNodes.length > 0 ? (
                editableNodes.samplerNodes.map(node => (
                  <SamplerNode 
                    key={node.nodeId} 
                    node={node} 
                    onNodeChange={handleNodeChange} 
                  />
                ))
              ) : (
                <Text type="secondary">没有找到采样器节点</Text>
              )}
            </Tabs.TabPane>
          </Tabs>
        </Card>
      </Content>
    </Layout>
  );
};

export default WorkflowDetailPage; 